import React from 'react';
import { Award, Globe, Heart, Scale, Shield, Users } from 'lucide-react';
import type { InfoPageData } from '../../api/infoPages';

const SERVER = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';

function mediaUrl(path?: string) {
  if (!path?.trim()) return '';
  if (path.startsWith('http')) return path;
  return `${SERVER}${path.startsWith('/') ? path : `/${path}`}`;
}

const ICONS: Record<string, React.ElementType> = {
  heart: Heart,
  award: Award,
  users: Users,
  globe: Globe,
  shield: Shield,
  scale: Scale,
};

function heroGradient(pageKey: InfoPageData['pageKey']) {
  if (pageKey === 'legal') return 'from-gray-700 via-gray-800 to-gray-900';
  if (pageKey === 'privacy') return 'from-blue-600 via-blue-700 to-indigo-800';
  return 'from-blue-600 to-purple-600';
}

function renderParagraphs(text: string) {
  const blocks = text.split(/\n\n+/).filter(Boolean);
  return blocks.map((block, i) => {
    const lines = block.split('\n').filter(Boolean);
    const isList = lines.every((l) => l.startsWith('•') || l.startsWith('-'));
    if (isList) {
      return (
        <ul key={i} className="list-disc pl-6 space-y-2 mt-2">
          {lines.map((l, j) => (
            <li key={j}>{l.replace(/^[•-]\s*/, '')}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className={i > 0 ? 'mt-4' : ''}>
        {block}
      </p>
    );
  });
}

type Props = { data: InfoPageData };

const ManagedInfoPageView: React.FC<Props> = ({ data }) => {
  const HeroIcon = ICONS[data.page.heroIcon || 'shield'] || Shield;
  const banner = mediaUrl(data.page.bannerImageUrl);
  const opacity = data.page.bannerOverlayOpacity ?? 0.55;
  const sections = [...(data.sections || [])]
    .filter((s) => s.enabled !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const values = [...(data.values || [])]
    .filter((v) => v.enabled !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const stats = [...(data.stats || [])]
    .filter((s) => s.enabled !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 pt-16 pb-16 md:pb-0">
      <main className="flex-grow">
        <section
          className={`relative text-white py-16 md:py-20 bg-gradient-to-br ${heroGradient(data.pageKey)}`}
          style={
            banner
              ? {
                  backgroundImage: `linear-gradient(rgba(0,0,0,${opacity}), rgba(0,0,0,${opacity})), url(${banner})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : undefined
          }
        >
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <HeroIcon className="w-16 h-16 mx-auto mb-4 opacity-95" />
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{data.page.title}</h1>
              {data.page.subtitle ? (
                <p className={`text-xl ${data.pageKey === 'legal' ? 'text-gray-300' : 'text-blue-100'}`}>
                  {data.page.subtitle}
                </p>
              ) : null}
            </div>
          </div>
        </section>

        {data.layout === 'about' ? (
          <>
            {sections[0] ? (
              <section className="py-16">
                <div className="container mx-auto px-4">
                  <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl font-bold mb-8">{sections[0].title}</h2>
                    <div className="text-lg text-gray-600 leading-relaxed">
                      {renderParagraphs(sections[0].content)}
                    </div>
                  </div>
                </div>
              </section>
            ) : null}

            {values.length > 0 ? (
              <section className="py-16 bg-white">
                <div className="container mx-auto px-4">
                  <h2 className="text-3xl font-bold text-center mb-12">Our Values</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {values.map((v) => {
                      const Icon = ICONS[v.icon] || Heart;
                      return (
                        <div key={v.title} className="text-center">
                          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Icon className="w-8 h-8 text-blue-600" />
                          </div>
                          <h3 className="text-xl font-semibold mb-2">{v.title}</h3>
                          <p className="text-gray-600">{v.description}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            ) : null}

            {sections.slice(1).map((s) => (
              <section key={s.id} className="py-16">
                <div className="container mx-auto px-4">
                  <div className="max-w-4xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-8">{s.title}</h2>
                    <div className="text-gray-600 leading-relaxed">{renderParagraphs(s.content)}</div>
                  </div>
                </div>
              </section>
            ))}

            {stats.length > 0 ? (
              <section className="py-16 bg-blue-600 text-white">
                <div className="container mx-auto px-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                    {stats.map((s) => (
                      <div key={s.label}>
                        <div className="text-4xl font-bold mb-2">{s.value}</div>
                        <div className="text-xl">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            ) : null}
          </>
        ) : (
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
                {data.page.lastUpdated ? (
                  <p className="text-sm text-gray-500 mb-8">Last updated: {data.page.lastUpdated}</p>
                ) : null}
                <div className="prose prose-lg max-w-none text-gray-600">
                  {sections.map((s) => (
                    <div key={s.id} className="mt-8 first:mt-0">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">{s.title}</h2>
                      {renderParagraphs(s.content)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default ManagedInfoPageView;

